<script setup>
import { ref, watch, defineProps, defineEmits } from 'vue';
import BaseInput, { propsDefinition } from '@/components/BaseInput.vue';
import EyeIcon from '@/components/Icons/EyeIcon.vue';
import EyeSlashIcon from '@/components/Icons/EyeSlashIcon.vue';
import ExclamationCircleIcon from '@/components/Icons/ExclamationCircleIcon.vue';

const props = defineProps({
  ...propsDefinition,
  type: {
    type: [String],
    default: 'text',
    validation(value) {
      return validTextInputTypes.includes(value);
    }
  },
  togglePassword: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['input']);

const updateModelValue = (newValue, oldValue) => {
  emit('input', newValue, oldValue);
};

// == Model Value =======================================
const value = ref(props.value);
watch(
  () => props.value,
  (newValue) => {
    value.value = newValue;
  }
);
watch(value, updateModelValue);

// == Password Toggle =================================
const inputType = ref(props.type);
const toggleInputType = () => {
  inputType.value = inputType.value === 'password' ? 'text' : 'password';
};
</script>

<script>
export default {
  inheritAttrs: false
};

export const validTextInputTypes = [
  'button',
  'checkbox',
  'color',
  'date',
  'datetime-local',
  'email',
  'file',
  'hidden',
  'image',
  'month',
  'number',
  'password',
  'radio',
  'range',
  'reset',
  'search',
  'submit',
  'tel',
  'text',
  'time',
  'url',
  'week'
];
</script>

<template>
  <BaseInput v-bind="props" v-model="value">
    <template v-slot="{ inputAttributes, inputClass }">
      <div class="relative">
        <input
          v-model="value"
          :name="name"
          :type="inputType"
          :class="{
            [inputClass]: inputClass,
            '!border-brand-red': errorMessage,
            'bg-brand-white px-6 text-gray-900 placeholder:text-brand-gray focus:!outline-none focus:[box-shadow:none] focus:border-brand-secondary font-sans sm:leading-6 rounded-lg border-[#E3E3E3] h-[3.125rem]': !['checkbox', 'radio', 'range'].includes(inputAttributes.type),
            'pr-14': togglePassword || (!togglePassword && errorMessage),
            'is-invalid': !isValid
          }"
          v-bind="{ ...inputAttributes, ...$attrs }"
          class="w-full border"
        />
        <ExclamationCircleIcon v-if="!togglePassword && errorMessage" class="absolute top-1/2 -translate-y-1/2 right-6 text-brand-red w-[30px] h-[30px] z-10" />
        <button v-if="togglePassword" type="button" class="absolute top-1/2 -translate-y-1/2 right-6 z-10" @click="toggleInputType">
          <span class="sr-only">Toggle Password</span>
          <Component :is="inputType === 'password' ? EyeIcon : EyeSlashIcon" class="block text-black w-[30px] h-[30px] mb-0" />
        </button>
      </div>
    </template>
  </BaseInput>
</template>
